// The layers list: select, drag-to-reorder, toggle visibility/lock, and a per-selected-layer
// action row (forward / back / duplicate / delete). Top of the list = front-most (highest z),
// like every design tool. All chrome is T3 (lucide icons + tokens — no emoji glyphs).

import { useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  GripVertical,
  Image as ImageIcon,
  Layers as LayersIcon,
  Lock,
  LockOpen,
  Sparkles,
  Square,
  Stamp,
  Trash2,
  Type,
  type LucideIcon,
} from 'lucide-react'
import type { CompositionEditor } from './use-composition-editor'
import type { Layer } from '../types'
import { useConfirmableDelete } from './confirm-delete'

const TYPE_ICON: Record<Layer['type'], LucideIcon> = {
  text: Type,
  image: ImageIcon,
  logo: Stamp,
  shape: Square,
  overlay: LayersIcon,
  decor: Sparkles,
}

function layerLabel(l: Layer): string {
  if (l.name) return l.name
  if (l.type === 'text')
    return (
      l.runs
        .map(r => r.text)
        .join(' ')
        .slice(0, 24) || 'Text'
    )
  return l.type[0]!.toUpperCase() + l.type.slice(1)
}

/** A small T3 ghost icon button — the in-panel action language (no emoji). */
function IconBtn({
  icon: Icon,
  title,
  onClick,
  active,
  danger,
  disabled,
}: {
  icon: LucideIcon
  title: string
  onClick: (e: React.MouseEvent) => void
  active?: boolean
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-7 w-7 items-center justify-center rounded-[7px] transition-colors disabled:opacity-30 ${
        danger
          ? 'text-paper-dim hover:bg-bg-3 hover:text-red-400'
          : active
            ? 'text-paper hover:bg-bg-3'
            : 'text-paper-dim hover:bg-bg-3 hover:text-paper'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

export function LayersPanel({ editor }: { editor: CompositionEditor }) {
  const ordered = [...editor.comp.layers].sort((a, b) => b.z - a.z)
  const { confirmingId, requestDelete } = useConfirmableDelete(editor)
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const selected = editor.selected

  return (
    <div className="flex flex-col">
      <div className="px-4 pt-3 pb-2 text-xs font-semibold text-paper-dim">Layers</div>

      <div className="flex flex-col">
        {ordered.map(l => {
          const active = l.id === editor.selectedId
          const Icon = TYPE_ICON[l.type]
          const dragging = dragId === l.id
          const isOver = overId === l.id && dragId != null && dragId !== l.id
          return (
            <div
              key={l.id}
              draggable
              onDragStart={e => {
                setDragId(l.id)
                e.dataTransfer.effectAllowed = 'move'
              }}
              onDragOver={e => {
                e.preventDefault()
                if (overId !== l.id) setOverId(l.id)
              }}
              onDrop={e => {
                e.preventDefault()
                if (dragId && dragId !== l.id) editor.moveLayer(dragId, l.id)
                setDragId(null)
                setOverId(null)
              }}
              onDragEnd={() => {
                setDragId(null)
                setOverId(null)
              }}
              onClick={() => editor.select(l.id)}
              className={`group flex cursor-pointer items-center gap-1.5 px-2 py-1.5 text-sm ${
                active ? 'bg-acid-soft text-paper' : 'text-paper-dim hover:bg-bg-3'
              } ${dragging ? 'opacity-40' : ''} ${isOver ? 'border-t-2 border-acid' : 'border-t-2 border-transparent'}`}
            >
              <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-paper-faint opacity-0 group-hover:opacity-100" />
              <Icon className="h-3.5 w-3.5 shrink-0 text-paper-faint" />
              <span className="flex-1 truncate">{layerLabel(l)}</span>
              <button
                type="button"
                title={l.hidden ? 'Show' : 'Hide'}
                aria-label={l.hidden ? 'Show' : 'Hide'}
                onClick={e => {
                  e.stopPropagation()
                  editor.toggleHidden(l.id)
                }}
                className={`flex h-6 w-6 items-center justify-center rounded-[6px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper ${
                  l.hidden ? 'text-paper-faint opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
              >
                {l.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                title={l.locked ? 'Unlock' : 'Lock'}
                aria-label={l.locked ? 'Unlock' : 'Lock'}
                onClick={e => {
                  e.stopPropagation()
                  editor.toggleLock(l.id)
                }}
                className={`flex h-6 w-6 items-center justify-center rounded-[6px] text-paper-dim transition-colors hover:bg-bg-2 hover:text-paper ${
                  l.locked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
              >
                {l.locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
              </button>
            </div>
          )
        })}
      </div>

      {/* Per-selected-layer actions — icon buttons, part of the Layers component. */}
      {selected && (
        <div className="mt-1 flex items-center gap-1 border-t border-line px-2 py-2">
          <IconBtn
            icon={ChevronUp}
            title="Bring forward"
            onClick={() => editor.reorder(selected.id, 'forward')}
          />
          <IconBtn
            icon={ChevronDown}
            title="Send backward"
            onClick={() => editor.reorder(selected.id, 'backward')}
          />
          <IconBtn
            icon={Copy}
            title="Duplicate"
            onClick={() => editor.duplicateLayer(selected.id)}
          />
          <div className="flex-1" />
          <IconBtn
            icon={Trash2}
            danger
            title={confirmingId === selected.id ? 'Click again to delete' : 'Delete'}
            active={confirmingId === selected.id}
            onClick={() => requestDelete(selected)}
          />
        </div>
      )}
    </div>
  )
}
